import { BootResponse, OfferAPIResponse } from '@appcharge/shared-ui';
import { Offer } from 'common/contracts';

import type { OffersPreviewAPIResponse } from '../previewResponse.types';

export type { OffersPreviewAPIResponse, PreviewBalanceItem } from '../previewResponse.types';

type OfferUiWithSubType = Offer['offerUi'] & { offerUiSubType?: string };

const DEFAULT_OFFER_API_RESPONSE: Omit<OfferAPIResponse, 'offers'> = {
  balances: [],
  offerFocus: {},
  profileFrameImg: '',
  playerLevelImg: '',
  publisherSettings: {},
  bannerImage: '',
  playerLevels: [],
  tabs: [],
  logo: ''
};

function normalizePopupForPreview(popup: Offer): Offer {
  const offerUi = (popup.offerUi ?? {}) as OfferUiWithSubType;
  const subType = popup.subType ?? offerUi.offerUiSubType;
  if (subType != null && offerUi.offerUiSubType == null) {
    return {
      ...popup,
      offerUi: { ...offerUi, offerUiSubType: subType } as Offer['offerUi']
    };
  }
  return popup;
}

export function mapPreviewResponseToOfferAPIResponse(
  response: Record<string, unknown>
): OfferAPIResponse {
  const preview = response as OffersPreviewAPIResponse;
  const offers = preview.offers ?? {};
  const popups = (offers.popups ?? []).map(normalizePopupForPreview);
  const balances = (preview.balances ?? []).map((balance) => ({
    ...balance,
    quantity: balance.quantity ?? '',
    formattedAmount: balance.formattedAmount ?? balance.formattedQuantity ?? ''
  }));

  return {
    ...DEFAULT_OFFER_API_RESPONSE,
    offers: {
      popups,
      promotions: offers.promotions ?? [],
      bundles: offers.bundles ?? [],
      rolling: offers.rolling ?? [],
      progressBars: offers.progressBars ?? []
    },
    balances,
    sections: preview.sections ?? [],
    offerFocus: preview.offerFocus ?? {},
    profileFrameImg: preview.profileFrameImg ?? '',
    playerLevelImg: preview.playerLevelImg ?? '',
    publisherSettings: preview.publisherSettings ?? {},
    bannerImage: preview.bannerImage ?? '',
    playerLevels: preview.playerLevels ?? [],
    tabs: preview.tabs ?? [],
    logo: preview.logo ?? ''
  };
}

export function mergePreviewStoreThemeIntoBoot(
  boot: BootResponse,
  response: Record<string, unknown>
): BootResponse {
  const preview = response as OffersPreviewAPIResponse;
  const storeTheme = preview.storeTheme;
  if (!storeTheme || !boot.storeTheme?.general) {
    return boot;
  }

  const bgDesktop =
    storeTheme.bgImageDesktop?.trim() ||
    boot.storeTheme.general.backgroundImageDesktop;
  const bgMobile =
    storeTheme.bgImageMobile?.trim() ||
    boot.storeTheme.general.backgroundImageMobile;
  const logo = storeTheme.logo?.trim() || boot.storeTheme.general.logo;

  return {
    ...boot,
    storeTheme: {
      ...boot.storeTheme,
      general: {
        ...boot.storeTheme.general,
        backgroundImageDesktop: bgDesktop,
        backgroundImageMobile: bgMobile,
        logo
      }
    }
  };
}
